import { useMemo, useState } from "react";
import { Search, FileText, ArrowUpRight } from "lucide-react";
import { useLang } from "../../lang";
import { PRODUCTS, CATEGORIES, isRender } from "../../catalog";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function groupTitle(slug, lang) {
  const c = CATEGORIES[slug];
  const parent = c?.parent && CATEGORIES[c.parent];
  return { family: parent ? parent[lang].title : c?.[lang].title, title: c?.[lang].title || slug };
}

function SheetRow({ p, lang }) {
  const l = p[lang] || p.it;
  return (
    <a
      href={p.brochure}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 py-3.5 px-2 -mx-2 rounded-xl hover:bg-[#f5f5f7] transition-colors duration-200"
      data-testid={`sheet-${p.slug}`}
    >
      <div className="w-14 h-11 rounded-lg bg-white border border-[#e4e4e7] flex-shrink-0 overflow-hidden">
        <img src={p.image} alt="" loading="lazy" className={`w-full h-full ${isRender(p.image) ? "object-contain p-1" : "object-cover"}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold text-[#1d1d1f] truncate">{l.name}</div>
        <div className="text-[12.5px] text-[#6e6e73] truncate">{l.tagline}</div>
      </div>
      <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1D3F94] shrink-0">
        <FileText size={15} strokeWidth={1.75} />
        <span className="hidden sm:inline">PDF</span>
        <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </a>
  );
}

export default function DatasheetLibrary() {
  const { lang } = useLang();
  const it = lang === "it";
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");

  const all = useMemo(() => Object.values(PRODUCTS).filter((p) => p.brochure), []);
  const families = useMemo(() => [...new Set(all.map((p) => groupTitle(p.category, lang).family))], [all, lang]);

  const groups = useMemo(() => {
    const nq = norm(q.trim());
    const out = [];
    for (const p of all) {
      const g = groupTitle(p.category, lang);
      if (family !== "all" && g.family !== family) continue;
      const hay = norm(`${p.it.name} ${p.it.tagline} ${g.title} ${g.family}`);
      if (nq && !hay.includes(nq)) continue;
      let grp = out.find((x) => x.key === p.category);
      if (!grp) out.push((grp = { key: p.category, ...g, items: [] }));
      grp.items.push(p);
    }
    return out;
  }, [all, q, family, lang]);

  const count = groups.reduce((a, g) => a + g.items.length, 0);
  const chip = (active) => `text-[13px] px-3.5 py-1.5 rounded-full border transition-colors duration-200 whitespace-nowrap ${active ? "bg-[#1D3F94] text-white border-[#1D3F94]" : "bg-white text-[#1d1d1f] border-[#d2d2d7] hover:border-[#1D3F94]"}`;

  return (
    <section className="mb-6 bg-white rounded-3xl border border-[#d2d2d7] p-5 md:p-8" id="schede" data-testid="datasheet-library">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="text-[24px] md:text-[28px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{it ? "Schede tecniche" : "Datasheets"}</h2>
          <p className="mt-1 text-[14px] text-[#6e6e73]" data-testid="datasheet-count">
            {count} {it ? (count === 1 ? "scheda disponibile" : "schede disponibili") : "datasheets"} · ASAŞ
          </p>
        </div>
        <label className="relative w-full md:w-[320px]">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6e6e73]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={it ? "Cerca sistema, es. RWT95, scorrevole…" : "Search system…"}
            className="w-full bg-[#f5f5f7] rounded-full pl-10 pr-4 py-2.5 text-[14px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/30"
            data-testid="datasheet-search"
          />
        </label>
      </div>

      <div className="mt-5 -mx-1 px-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" data-testid="datasheet-filters">
        <button type="button" onClick={() => setFamily("all")} className={chip(family === "all")} data-testid="datasheet-filter-all">{it ? "Tutte" : "All"}</button>
        {families.map((f) => (
          <button type="button" key={f} onClick={() => setFamily(f)} className={chip(family === f)} data-testid={`datasheet-filter-${norm(f).replace(/[^a-z0-9]+/g, "-")}`}>{f}</button>
        ))}
      </div>

      {count === 0 ? (
        <p className="py-14 text-center text-[14px] text-[#6e6e73]" data-testid="datasheet-empty">{it ? "Nessuna scheda corrisponde alla ricerca." : "No datasheet matches your search."}</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8">
          {groups.map((g) => (
            <div key={g.key} data-testid={`datasheet-group-${g.key}`}>
              <div className="flex items-baseline justify-between border-b border-[#e4e4e7] pb-2 mb-1">
                <h3 className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[#1d1d1f]">
                  {g.family !== g.title && <span className="text-[#6e6e73] font-medium">{g.family} · </span>}{g.title}
                </h3>
                <span className="text-[12px] text-[#6e6e73]">{g.items.length}</span>
              </div>
              {g.items.map((p) => <SheetRow key={p.slug} p={p} lang={lang} />)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
